import { fromEvent, of, switchMap } from 'rxjs';

import { catchError, filter, tap } from 'rxjs/operators';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from './services/auth.service';
import { ImagingCenter } from '../model';
import { AppConfigService } from '../app-config.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'ft-login',
    templateUrl: './login.component.html',
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        TranslateModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        FormsModule,
        MatButtonModule,
    ],
    styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
    title = 'Login';
    form: FormGroup;
    errorMsg: string;
    hide: boolean = true;
    logo: string;
    readonly centers: ImagingCenter[];
    selectedCenter: ImagingCenter;

    #router = inject(Router);
    #authService = inject(AuthService);
    #fb = inject(FormBuilder);
    #config = inject(AppConfigService);
    #translate = inject(TranslateService);
    #destroyRef = inject(DestroyRef);

    constructor() {
        this.logo = this.#config.logo;
        this.centers = this.#config.centers;
        this.selectedCenter = this.centers[0];

        this.form = this.#fb.group({
            username: ['', Validators.compose([Validators.required])],
            password: ['', Validators.compose([Validators.required])],
        });
    }

    ngOnInit() {
        fromEvent(document.getElementById('login'), 'keyup')
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((event: KeyboardEvent) => {
                if (event.code === 'Enter') this.onSubmit();
            });
    }

    onSubmit() {
        // Set the default center, the one which will be sent in header for each request.
        localStorage.setItem('ImagingCenter', this.selectedCenter.id ?? '1');
        this.errorMsg = null;

        this.#authService
            .login(this.form.value)
            .pipe(
                tap((value) => {
                    if (!value) this.errorMsg = this.#translate.instant('login.incorrect');
                }),
                filter((value) => value),
                switchMap(() => this.#authService.checkUser()),
                catchError((err) => of(err)),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe(() => this.#router.navigateByUrl(this.#authService.redirectUrl));
    }
}
